import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Text, H2 } from "../../../../../components/typography";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button } from "@amzn/stencil-react-components/button";
import { UploadCard } from "../upload-card";
import useArb from "../../../../../hooks/use-arb";
import { AttachmentType, UploadDetails } from "../../models";
import { submitDocument } from "../../requests/requests";
import { AttachmentSubType } from "../../models";
import { deleteCachedRequest, getCandidateData } from "../../../../../utility/candidate-data";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";

export function CurpUploadLayout() {
  const bundle = useArb("HVHChecklistPantherResource");
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const history = useHistory();
  const [frontErrorMessage, setFrontErrorMessage] = useState<string>();
  const [backErrorMessage, setBackErrorMessage] = useState<string>();
  const [dependencyServiceErrorMessage, setDependencyServiceErrorMessage] = useState<string>();
  const backToMainPage = () => {
    const path = `/checklist/${requisitionId}/${applicationId}`;
    history.push(path);
  };

  const curpIdUploadButtonTextMessage = bundle.getMessage("Checklist-Mainpage-CURP-ID-Upload-ButtonText");
  const curpIdMessage = bundle.getMessage("Checklist-CURP-ID-Text");
  const documentImageSubmitMessage = bundle.getMessage("Checklist-Document-Image-Submit-ButtonText");

  const documentMaximumFileSizeMessage = bundle.getMessage("Checklist-Document-Maximum-File-Size-Text");
  const documentAcceptableFileMessage = bundle.getMessage("Checklist-Document-Acceptable-File-Text");
  const p0LimitFixMessage = bundle.getMessage("p0LimitFixMessage");
  const documentUploadDependencyServiceErrorMessage = bundle.getMessage("Checklist-Document-General-Error-Message");
  const [frontDocument, setFrontDocument] = useState<UploadDetails>();
  const [backDocument, setBackDocumnet] = useState<UploadDetails>();
  const [blockSubmitButton, setBlockSubmitButton] = useState<boolean>(true);

  useEffect(() => {
    // Back side is optional but should disable when back error message present
    setBlockSubmitButton(!frontDocument?.imageBase64 || !!backErrorMessage);
  }, [frontDocument, backErrorMessage]);

  const updateDocument = async (
    image: string,
    fileName: string,
    contentType: string,
    side: string,
    applicationId?: string
  ) => {
    let candidateId = "";
    if (applicationId) {
      const candidateData = await getCandidateData(applicationId);
      candidateId = candidateData?.candidateId;
    }

    const documentData = {
      documentType: AttachmentType.CURP,
      attachmentSubType: side,
      imageBase64: image,
      fileName: fileName,
      contentType: contentType,
      candidateId: candidateId,
    };

    if (side === AttachmentSubType.FRONT) {
      setFrontDocument(documentData);
    } else {
      setBackDocumnet(documentData);
    }
  };

  const clickEventSubmitDocument = async () => {
    let returnToMainPage = true;
    if (frontDocument) {
      try {
        const docs = [frontDocument];
        // Back is optional currently
        if (backDocument?.imageBase64) {
          docs.push(backDocument);
        }
        await submitDocument(applicationId, docs, setBlockSubmitButton);
      } catch (err) {
        console.log(err);
        setBlockSubmitButton(false);
        setDependencyServiceErrorMessage(documentUploadDependencyServiceErrorMessage);
        returnToMainPage = false;
      }
    }

    deleteCachedRequest(applicationId); // delete stale data so that updated Id loads on home page
    if (returnToMainPage) {
      backToMainPage();
    }
  };

  return (
    <Col padding={"10px"} display={"flex"} justifyContent={"center"} gridGap={6} maxWidth={"330px"}>
      <H2>{curpIdUploadButtonTextMessage}</H2>
      <Text>
        {curpIdMessage} <strong style={{ color: "red" }}>*</strong>{" "}
      </Text>
      <Text>{documentAcceptableFileMessage}</Text>
      <Text>{documentMaximumFileSizeMessage}</Text>
      <Row padding={{ top: "10px", bottom: "10px" }} gridGap="S400">
        <UploadCard
          side={AttachmentSubType.FRONT}
          documentDetailsFn={updateDocument}
          setErrorMessage={setFrontErrorMessage}
          setDependencyErrorMessage={setDependencyServiceErrorMessage}
          applicationId={applicationId}
        />
        <UploadCard
          side={AttachmentSubType.BACK}
          optional={true}
          documentDetailsFn={updateDocument}
          setErrorMessage={setBackErrorMessage}
          setDependencyErrorMessage={setDependencyServiceErrorMessage}
          applicationId={applicationId}
        />
      </Row>
      {frontErrorMessage || backErrorMessage || dependencyServiceErrorMessage ? (
        <Row padding={{ top: "10px", bottom: "10px" }}>
          <MessageBanner type={MessageBannerType.Error}>
            {dependencyServiceErrorMessage ?? frontErrorMessage ?? backErrorMessage}
          </MessageBanner>
        </Row>
      ) : null}
      <Row padding={{ top: "10px", bottom: "10px" }}>
        <MessageBanner type={MessageBannerType.Informational}>{p0LimitFixMessage}</MessageBanner>
      </Row>
      <Col padding={"10px"}>
        <Button onClick={clickEventSubmitDocument} aria-disabled={blockSubmitButton}>
          {documentImageSubmitMessage}
        </Button>
      </Col>
    </Col>
  );
}
